import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
} from '@mui/material';
import API from '../config/axiosConfig';
import DepartmentSelectionStep from './DepartmentSelectionStep';
import ConfirmationDialog from './dialogs/ConfirmationDialog';
import DropdownWithIcon from './DropdownWithIcon';
import hospitalIcon from '../assets/protocols/hospital.svg';
import { getServerHebMessage } from '../utils/department-utils';

const DepartmentSelectionDialog = ({ open, onClose, onSuccess }) => {
  const location = useLocation();
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState('');
  const [departmentOption, setDepartmentOption] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [newDepartment, setNewDepartment] = useState({
    nameHebrew: '',
    nameEnglish: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Reported by DepartmentSelectionStep, which shows the message next to the offending field.
  const [duplicateNameErrors, setDuplicateNameErrors] = useState({});

  // Load hospitals on component mount
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const response = await API.get('/api/onboarding/hospitals');
        const params = new URLSearchParams(location.search);
        const isTest = params.get('test') === 'true';
        const filteredHospitals = isTest
          ? response.data
          : response.data.filter(h => h.nameEnglish !== 'test hospital');
        setHospitals(filteredHospitals);
      } catch (err) {
        console.error('Failed to fetch hospitals:', err);
        setError('שגיאה בטעינת בתי החולים');
      }
    };
    fetchHospitals();
  }, []);

  const handleSubmit = async () => {
    setError('');
    
    // Validation
    if (!selectedHospital) {
      setError('נא לבחור בית חולים');
      return;
    }
    
    if (departmentOption === 'join' && !selectedDepartment) {
      setError('אנא בחר מחלקה');
      return;
    }
    
    if (departmentOption === 'create' && !newDepartment.nameHebrew) {
      setError('אנא הזן שם מחלקה בעברית');
      return;
    }

    // DepartmentSelectionStep already shows the message under the offending field.
    if (departmentOption === 'create' && Object.keys(duplicateNameErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (departmentOption === 'join') {
        await API.post('/api/users/request-to-join', {
          departmentId: selectedDepartment,
          hospitalId: selectedHospital
        });
      } else if (departmentOption === 'create') {
        await API.post(`/api/hospitals/${selectedHospital}/departments`, newDepartment, {
          handlingErrorLocally: true,
        });
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(getServerHebMessage(err) || 'אירעה שגיאה. אנא נסה שוב מאוחר יותר.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedHospital('');
    setDepartmentOption('join');
    setSelectedDepartment('');
    setNewDepartment({ nameHebrew: '', nameEnglish: '' });
    setError('');
    setDuplicateNameErrors({});
    onClose();
  };

  const handleNewDepartmentChange = (field, value) => {
    setNewDepartment(prev => ({ ...prev, [field]: value }));
  };

  return (
    <ConfirmationDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title="ניהול מחלקות"
      confirmText={isSubmitting ? 'מבצע...' : 'אישור'}
      cancelText="ביטול"
      maxWidth="md"
    >
      <div style={{ textAlign: 'right', direction: 'rtl' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>בחר בית חולים</Typography>
          <DropdownWithIcon
            icon={hospitalIcon}
            placeholder="בית חולים"
            value={selectedHospital}
            onChange={(e) => setSelectedHospital(e.target.value)}
            required
            error={!!error && !selectedHospital}
          >
            {hospitals.map((hospital) => (
              <option key={hospital._id} value={hospital._id}>
                {hospital.nameHebrew}
              </option>
            ))}
          </DropdownWithIcon>
        </Box>

        {selectedHospital && (
          <Box>
            <DepartmentSelectionStep
              selectedHospital={selectedHospital}
              onHospitalChange={setSelectedHospital}
              departmentOption={departmentOption}
              onDepartmentOptionChange={setDepartmentOption}
              selectedDepartment={selectedDepartment}
              onSelectedDepartmentChange={setSelectedDepartment}
              newDepartment={newDepartment}
              onNewDepartmentChange={handleNewDepartmentChange}
              onDuplicateNameChange={setDuplicateNameErrors}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              error={error}
              showSubmitButton={false}
              noDepartmentsMessage={`טרם נוצרו מחלקות בבית חולים ${hospitals.find(h => h._id === selectedHospital)?.nameHebrew || ''}. נוכל לפתוח מחלקה ראשונה חדשה יחד, אליה ניתן יהיה  להזמין את שאר הצוות, להעלות פרוטוקולי טיפול, מקרים רפואיים מעניינים ולהשתמש בשאר השירותים שלנו.`}
            />
          </Box>
        )}
      </div>
    </ConfirmationDialog>
  );
};

export default DepartmentSelectionDialog;

DepartmentSelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
